import { adViewApi } from '../../../api/adViewApi';
import { fireToast } from '../../../helpers/Toasts';
import { PAGE } from '../../../helpers/pages';
import { logger } from '@dist-marketplace-frontend/services';
import { VERTICAL } from '../../../helpers/verticals';
import { useVerticalContext } from '../../../contexts/VerticalContext';
import { useQuery } from '@tanstack/react-query';
import { HomePageData, transformHomePageResponse } from '../helpers';
import { getRecommendationId } from '../../../helpers/getRecommendationId';
import { useUserContext } from '../../../contexts/UserContext';
import { formatAuthorizationHeader } from '../../../helpers/auth';
import type { IncomingHttpHeaders } from 'node:http';

export type Section = {
  id?: number;
  name: string;
  displayName: string;
  link?: string;
  query?: { [key: string]: string };
};

type UseHomePageProps = {
  init: HomePageData;
};

const useHomePage = (props: UseHomePageProps) => {
  const { init } = props;
  const { vertical } = useVerticalContext();
  const { user, accessToken } = useUserContext();

  const query = useQuery({
    queryKey: ['homePageData', vertical],
    queryFn: () => updateHomePageData(vertical, user, accessToken),
    // The initial data exist only for the vertical that was used during server side rendering
    initialData: init.vertical === vertical ? init : undefined,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  return { homePageData: query.data, ...query };
};

const updateHomePageData = async (
  vertical: VERTICAL,
  user?: { id?: string } | null,
  accessToken?: string,
) => {
  try {
    const recommendationId = getRecommendationId(undefined, user);
    const headers: IncomingHttpHeaders = formatAuthorizationHeader(accessToken);

    if (recommendationId) {
      headers['dd_recommendation_user'] = recommendationId;
    }

    const { data } = await adViewApi.getHomePageData(vertical, headers);

    return transformHomePageResponse(data);
  } catch (error) {
    logger.error({
      error: error,
      message: 'Error handling updateHomePageData',
      tags: [PAGE.HOME],
    });
    fireToast({
      type: 'ERROR',
      text: 'Oops! Something went wrong, please try again later',
    });
    throw error;
  }
};

export { useHomePage };
